// src/components/AnalyticsCharts.js
import React, { useState } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import { 
  PieChart as PieIcon, 
  BarChart3, 
  TrendingUp,
  Download,
  Filter
} from 'lucide-react';

const AnalyticsCharts = ({ portfolio, darkMode }) => {
  const [activeChart, setActiveChart] = useState('sector');
  const [timeRange, setTimeRange] = useState('1m');

  // Calculate analytics data
  const sectorAllocation = calculateSectorAllocation(portfolio);
  const performanceData = generatePerformanceData(timeRange);
  const topPerformers = getTopPerformers(portfolio);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  const chartConfigs = {
    sector: {
      title: 'Sector Allocation',
      icon: PieIcon,
      chart: (
        <PieChart>
          <Pie
            data={sectorAllocation}
            cx="50%"
            cy="50%"
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
          >
            {sectorAllocation.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Value']} />
        </PieChart>
      )
    },
    performance: {
      title: 'Performance Trend',
      icon: TrendingUp,
      chart: (
        <LineChart data={performanceData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
          <Line 
            type="monotone" 
            dataKey="return" 
            stroke="#8884d8" 
            strokeWidth={2}
          />
          <Area type="monotone" dataKey="return" fill="#8884d8" fillOpacity={0.3} />
        </LineChart>
      )
    },
    topPerformers: {
      title: 'Top Performers',
      icon: BarChart3,
      chart: (
        <BarChart data={topPerformers}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="symbol" />
          <YAxis />
          <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'P&L']} />
          <Bar dataKey="pnl" fill="#00C49F">
            {topPerformers.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.pnl >= 0 ? '#00C49F' : '#FF8042'} 
              />
            ))}
          </Bar>
        </BarChart>
      )
    }
  };

  const exportChartData = () => {
    const data = {
      sectorAllocation,
      performanceData,
      topPerformers,
      exportDate: new Date().toISOString()
    };
    
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = 'portfolio-analytics.json';
    link.click();
  };

  return (
    <div className={`rounded-lg shadow-lg p-6 ${
      darkMode ? 'bg-gray-800' : 'bg-white'
    }`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h3 className="text-2xl font-bold">Portfolio Analytics</h3>
        
        <div className="flex flex-wrap gap-2">
          {/* Time Range Selector */}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className={`px-3 py-2 rounded border text-sm ${
              darkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="1w">1 Week</option>
            <option value="1m">1 Month</option>
            <option value="3m">3 Months</option>
            <option value="6m">6 Months</option>
            <option value="1y">1 Year</option>
          </select>

          {/* Chart Type Selector */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {Object.entries(chartConfigs).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <button
                  key={key}
                  onClick={() => setActiveChart(key)}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeChart === key
                      ? darkMode
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-blue-600 shadow'
                      : darkMode
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon size={16} className="mr-2" />
                  {config.title}
                </button>
              );
            })}
          </div>

          {/* Export Button */}
          <button
            onClick={exportChartData}
            className={`flex items-center px-3 py-2 rounded border text-sm ${
              darkMode
                ? 'bg-green-700 text-white hover:bg-green-600'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            <Download size={16} className="mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Main Chart */}
      <div className="h-96 mb-6">
        <ResponsiveContainer width="100%" height="100%">
          {chartConfigs[activeChart].chart}
        </ResponsiveContainer>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Total Value"
          value={portfolio.reduce((sum, stock) => sum + stock.marketValue, 0)}
          format="currency"
          darkMode={darkMode}
        />
        <MetricCard
          title="Total P&L"
          value={portfolio.reduce((sum, stock) => sum + stock.unrealizedPnl, 0)}
          format="currency"
          darkMode={darkMode}
          isPositive={portfolio.reduce((sum, stock) => sum + stock.unrealizedPnl, 0) >= 0}
        />
        <MetricCard
          title="Avg Return"
          value={calculateAverageReturn(portfolio)}
          format="percentage"
          darkMode={darkMode}
          isPositive={calculateAverageReturn(portfolio) >= 0}
        />
        <MetricCard
          title="Stocks"
          value={portfolio.length}
          format="number"
          darkMode={darkMode}
        />
      </div>
    </div>
  );
};

// Helper components and functions
const MetricCard = ({ title, value, format, darkMode, isPositive }) => {
  const formatValue = (val, fmt) => {
    switch (fmt) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(val);
      case 'percentage':
        return `${val >= 0 ? '+' : ''}${val.toFixed(2)}%`;
      default:
        return val.toString();
    }
  };

  return (
    <div className={`p-4 rounded-lg text-center ${
      darkMode ? 'bg-gray-700' : 'bg-gray-50'
    }`}>
      <div className="text-sm text-gray-500 mb-2">{title}</div>
      <div className={`text-xl font-bold ${
        format === 'currency' || format === 'percentage' 
          ? isPositive 
            ? darkMode ? 'text-green-400' : 'text-green-600'
            : darkMode ? 'text-red-400' : 'text-red-600'
          : darkMode ? 'text-white' : 'text-gray-900'
      }`}>
        {formatValue(value, format)}
      </div>
    </div>
  );
};

const calculateSectorAllocation = (portfolio) => {
  const sectors = {};
  portfolio.forEach(stock => {
    const sector = getSector(stock.symbol);
    sectors[sector] = (sectors[sector] || 0) + stock.marketValue;
  });
  
  return Object.entries(sectors).map(([name, value]) => ({
    name,
    value
  }));
};

const getSector = (symbol) => {
  const sectorMap = {
    'AAPL': 'Technology', 'GOOGL': 'Technology', 'MSFT': 'Technology',
    'TSLA': 'Automotive', 'AMZN': 'E-Commerce', 'NVDA': 'Semiconductors',
    'JPM': 'Financial', 'V': 'Financial', 'DIS': 'Entertainment',
    'NFLX': 'Entertainment', 'IBM': 'Technology', 'INTC': 'Semiconductors'
  };
  return sectorMap[symbol] || 'Other';
};

const generatePerformanceData = (timeRange) => {
  const data = [];
  const points = timeRange === '1w' ? 7 : timeRange === '1m' ? 30 : 90;
  let cumulativeReturn = 0;
  
  for (let i = 0; i < points; i++) {
    const dailyReturn = (Math.random() - 0.5) * 2;
    cumulativeReturn += dailyReturn;
    
    data.push({
      date: `Day ${i + 1}`,
      return: cumulativeReturn
    });
  }
  
  return data;
};

const getTopPerformers = (portfolio) => {
  return portfolio
    .sort((a, b) => b.unrealizedPnl - a.unrealizedPnl)
    .slice(0, 5)
    .map(stock => ({
      symbol: stock.symbol,
      pnl: stock.unrealizedPnl
    }));
};

const calculateAverageReturn = (portfolio) => {
  if (portfolio.length === 0) return 0;
  const totalReturn = portfolio.reduce((sum, stock) => {
    const cost = stock.average_cost * stock.quantity;
    return cost > 0 ? sum + (stock.unrealizedPnl / cost) * 100 : sum;
  }, 0);
  return totalReturn / portfolio.length;
};

export default AnalyticsCharts;